/**
 * VarietyMultiSelect — chip-based multi-select with typeahead and free-form
 * "Add other…" entry. Pass a `suggestions` list (e.g., from the category's
 * variation schema) and a `value` array; onChange emits string[].
 *
 * Also supports colour objects when `kind="colour"` — value/suggestions are
 * {name, hex} objects and emitted as such.
 */
import { useMemo, useRef, useState } from "react";
import { Plus, X } from "lucide-react";

const same = (a, b, kind) =>
  kind === "colour"
    ? (a?.name || "").trim().toLowerCase() === (b?.name || "").trim().toLowerCase()
    : String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

export const VarietyMultiSelect = ({
  label,
  value = [],
  suggestions = [],
  onChange,
  kind = "text", // "text" | "colour"
  placeholder = "Type to add…",
  testid = "variety-select",
}) => {
  const [q, setQ] = useState("");
  const [colourHex, setColourHex] = useState("#EEE7DA");
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return suggestions.filter((s) => {
      const label = kind === "colour" ? s?.name : s;
      const matches = !query || String(label).toLowerCase().includes(query);
      const alreadyIn = value.some((v) => same(v, s, kind));
      return matches && !alreadyIn;
    });
  }, [q, suggestions, value, kind]);

  const add = (item) => {
    if (!item) return;
    if (value.some((v) => same(v, item, kind))) return;
    onChange?.([...value, item]);
    setQ("");
    setColourHex("#EEE7DA");
    inputRef.current?.focus();
  };

  const addCustom = () => {
    const trimmed = q.trim();
    if (!trimmed) return;
    if (kind === "colour") add({ name: trimmed, hex: colourHex });
    else add(trimmed);
  };

  const remove = (i) => onChange?.(value.filter((_, idx) => idx !== i));

  return (
    <div className="col-span-12" data-testid={testid}>
      <div className="overline text-forest/60 mb-1.5 flex items-center justify-between">
        <span>{label}</span>
        {value.length > 0 && <span className="text-forest/40 text-[10px] normal-case">{value.length} selected</span>}
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testid}-chips`}>
          {value.map((v, i) => (
            <span
              key={`${kind === "colour" ? v?.name : v}-${i}`}
              className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 bg-cream border border-hairline rounded-full text-forest text-sm"
              data-testid={`${testid}-chip-${i}`}
            >
              {kind === "colour" && (
                <span
                  className="w-3 h-3 rounded-full border border-forest/20"
                  style={{ background: v?.hex || "#EEE" }}
                />
              )}
              {kind === "colour" ? v?.name : v}
              <button
                type="button"
                onClick={() => remove(i)}
                className="p-0.5 rounded-full hover:bg-forest hover:text-white"
                aria-label={`Remove ${kind === "colour" ? v?.name : v}`}
                data-testid={`${testid}-remove-${i}`}
              >
                <X size={11} strokeWidth={2} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        {kind === "colour" && (
          <input
            type="color"
            value={colourHex}
            onChange={(e) => setColourHex(e.target.value)}
            className="w-9 h-9 rounded-md border border-hairline bg-transparent cursor-pointer p-0"
            data-testid={`${testid}-hex`}
            aria-label="Colour hex"
          />
        )}
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder={placeholder}
          className="flex-1 bg-transparent border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest"
          data-testid={`${testid}-input`}
        />
        <button
          type="button"
          onClick={addCustom}
          disabled={!q.trim()}
          className="inline-flex items-center gap-1 overline text-white bg-forest rounded-full px-3 py-2 text-xs disabled:opacity-40"
          data-testid={`${testid}-add-btn`}
        >
          <Plus size={11} strokeWidth={2} /> Add
        </button>
      </div>

      {filtered.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testid}-suggestions`}>
          <span className="text-forest/40 text-[11px] uppercase tracking-wider self-center">Suggestions:</span>
          {filtered.slice(0, 20).map((s, i) => (
            <button
              key={`${kind === "colour" ? s?.name : s}-${i}`}
              type="button"
              onClick={() => add(s)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-hairline rounded-full text-forest/70 text-xs hover:border-forest hover:text-forest"
              data-testid={`${testid}-suggest-${i}`}
            >
              {kind === "colour" && (
                <span
                  className="w-2.5 h-2.5 rounded-full border border-forest/20"
                  style={{ background: s?.hex || "#EEE" }}
                />
              )}
              {kind === "colour" ? s?.name : s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default VarietyMultiSelect;
